import { RestError } from '@azure/core-http';
import { BatchImportUsersActionProps } from './batch-import-action.component';
import { useImmer } from 'use-immer';
import { useMutationBatchUploadCustomers, useMutationBatchUploadOccupants } from '../../../users.queries';
import { KnownBatchUploadArchiveMissingOption, KnownUserDtoUserType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import { mapProjectTypeToUserType } from '../../../users.constants';
import { castDraft } from 'immer';
import { useMutationCsvReadFile } from './batch-import.queries';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';

export type BatchImportUsersActionState = {
  current: string;
  loading: boolean;
  error?: RestError;
  recordCount?: number;
  valid?: boolean;
  fileName?: string;
};

export type BatchImportUserFormState = {
  file?: string;
  isArchiveManagedUsers: boolean;
  isArchiveNonManagedUsers: boolean;
};

export interface BatchImportUsersActionHandlers {
  onCancelIntent(): void;
  onUpload(): void;
  onSubmit(form: BatchImportUserFormState): void;
  onDownloadTemplate(): void;
}

interface IntentToBatchImport extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT';
}

export interface BatchImportSuccess extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT_SUCCESS';
}

export interface BatchImportFileUploadSuccess extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT_FILE_UPLOAD_SUCCESS';
}

export interface BatchImportFileUploadFailure extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT_FILE_UPLOAD_FAILURE';
}

export interface BatchImportFailure extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT_FAILURE';
}

export interface BatchImportDenied extends BatchImportUsersActionState {
  current: 'BATCH_IMPORT_DENIED';
}
export type BatchImportActionStates =
  | IntentToBatchImport
  | BatchImportSuccess
  | BatchImportFailure
  | BatchImportFileUploadSuccess
  | BatchImportFileUploadFailure
  | BatchImportDenied;
export function useBatchImportAction({ currentProjectID, onAbort, onSuccess }: BatchImportUsersActionProps) {
  const [state, setState] = useImmer<BatchImportUsersActionState>({
    current: 'BATCH_IMPORT',
    loading: false
  });
  const form = useForm<BatchImportUserFormState>();

  const batchUploadCustomerMutation = useMutationBatchUploadCustomers();
  const batchUploadOccupantsMutation = useMutationBatchUploadOccupants();
  const readCsvFileMutation = useMutationCsvReadFile();
  const defaultProject = useQueryGetProject(currentProjectID);
  const isLoadingDefaultProject = defaultProject.isLoading;

  const memoizedUserType = useMemo(() => {
    const projectType = defaultProject.data?.projectType;
    return mapProjectTypeToUserType(projectType);
  }, [defaultProject.data?.projectType]);

  useEffect(() => {
    if (
      memoizedUserType !== KnownUserDtoUserType.Customer &&
      memoizedUserType !== KnownUserDtoUserType.Occupant &&
      !isLoadingDefaultProject
    ) {
      setState({ current: 'BATCH_IMPORT_DENIED', loading: false });
    }
  }, [memoizedUserType, setState, isLoadingDefaultProject]);

  const handlers: BatchImportUsersActionHandlers = {
    onCancelIntent() {
      onAbort();
    },
    onDownloadTemplate() {
      downloadTemplate(memoizedUserType);
    },
    onUpload() {
      setState({ current: 'BATCH_IMPORT_LOADING', loading: true });

      readCsvFileMutation.mutate(undefined, {
        onSuccess(file) {
          form.setValue('file', file.content);
          setState({ current: 'BATCH_IMPORT_FILE_UPLOAD_SUCCESS', loading: false, fileName: file.name });
        },
        onError() {
          setState({ current: 'BATCH_IMPORT_FILE_UPLOAD_FAILURE', loading: false });
        }
      });
      setTimeout(() => setState({ current: 'BATCH_IMPORT', loading: false }), 500);
    },
    onSubmit(form) {
      if (!form.file) {
        setState({ current: 'BATCH_IMPORT_FILE_UPLOAD_FAILURE', loading: false });
        return;
      }
      setState({ current: 'BATCH_IMPORT_LOADING', loading: true });
      switch (memoizedUserType) {
        case KnownUserDtoUserType.Customer:
          batchUploadCustomerMutation.mutate(
            {
              projectID: currentProjectID,
              archiveMissing: getCheckboxResult(form.isArchiveManagedUsers ?? false, form.isArchiveNonManagedUsers),
              csv: form.file
            },
            {
              onSuccess() {
                onSuccess();
              },
              onError(error) {
                setState((draft) => {
                  draft.loading = false;
                  draft.error = castDraft(error);
                });
              }
            }
          );
          break;
        case KnownUserDtoUserType.Occupant:
          batchUploadOccupantsMutation.mutate(
            {
              projectID: currentProjectID,
              archiveMissing: getCheckboxResult(form.isArchiveManagedUsers, form.isArchiveNonManagedUsers),
              csv: form.file
            },
            {
              onSuccess() {
                setState({ current: 'BATCH_IMPORT_SUCCESS', loading: false });
                onSuccess();
              },
              onError(error) {
                setState((draft) => {
                  draft.loading = false;
                  draft.error = castDraft(error);
                });
              }
            }
          );
          break;
        default:
          setState({ current: 'BATCH_IMPORT_DENIED', loading: false });
          break;
      }
    }
  };

  return [state, handlers, memoizedUserType, form] as const;
}

function downloadTemplate(userType: KnownUserDtoUserType) {
  const groupOption =
    userType === KnownUserDtoUserType.Customer
      ? 'groupName'
      : KnownUserDtoUserType.Occupant
      ? 'unitName'
      : 'unknownGroupType';
  const csvHeaders = `firstName,lastName,preferredName,maidenName,email,mobile,prefersLowerLockers,${groupOption},externalID\n`;
  const csvContent =
    csvHeaders +
    'Kaye,Totleben,,B,kaye@example.com,+61400000000,false,1,ID1\nRani,Smith,Ran,,rani@example.com,+61400000001,true,2,ID2';
  // Create a Blob object for the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv' });
  // Generate a temporary URL for the Blob object
  const url = window.URL.createObjectURL(blob);
  // Create a temporary link element
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'GroundfloorBatchUploadTemplate.csv');
  // Append the link to the body
  document.body.appendChild(link);
  link.click();
  // Clean up by removing the temporary link and revoking the URL
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function getCheckboxResult(
  isArchiveManagedUsers: boolean,
  isArchiveNonManagedUsers: boolean
): KnownBatchUploadArchiveMissingOption {
  if (isArchiveManagedUsers && isArchiveNonManagedUsers) return 'all' as KnownBatchUploadArchiveMissingOption;
  if (isArchiveManagedUsers) return 'managed-only' as KnownBatchUploadArchiveMissingOption;
  if (isArchiveNonManagedUsers) return 'not-managed-only' as KnownBatchUploadArchiveMissingOption;
  else return 'none' as KnownBatchUploadArchiveMissingOption;
}
